// Imported from the files directly, not the "@common" barrel this component is
// itself re-exported from, to keep the barrel out of its own dependency cycle.
import { useEffect, useMemo, useState } from "react";
import { useInvalidateNetsuiteCache } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { showError } from "./Toast";

/**
 * "Sync" — pull the tab's NetSuite data fresh instead of from cache.
 *
 * The Journal Entry and Check tabs decide each clinic's created / missing
 * status by searching NetSuite, and those searches are served from a shared
 * server-side SuiteQL cache (SUITEQL_CACHE_TTL, an hour by default). An entry
 * posted in NetSuite directly, or from someone else's session, therefore keeps
 * showing as missing here until that entry expires. This clears the cache and
 * refetches, so the status columns reflect NetSuite as of right now.
 *
 * The age shown is time since THIS session last synced, not the age of the
 * rows on screen. Those are two different things and only the first is
 * knowable here: the search endpoints return no `computed_at`, so an
 * unsynced page's rows may be anywhere from seconds to a full TTL old. Hence
 * the neutral "Sync with NetSuite" until a sync actually happens — better than
 * a client-side timestamp implying freshness it cannot vouch for. (Compare
 * CacheFreshness in ClinicCashFlow, which does have a real `computed_at`.)
 *
 * Organizer / admin only, enforced both by the callers — which render it only
 * for those roles — and by the endpoint: the flush is shared across every user
 * and both products, so it costs everyone else's cache hits.
 *
 * Props:
 *   source — short tag logged server-side to say which tab fired it.
 */
export default function SyncNetsuiteButton({ source = "manual sync" }) {
  const syncMut = useInvalidateNetsuiteCache();
  const syncing = syncMut.isPending;
  const [syncedAt, setSyncedAt] = useState(null);

  // Re-render once a minute so "3m ago" does not sit there saying "just now"
  // for the rest of the session. Only while there is something to age.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!syncedAt) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, [syncedAt]);

  const age = useMemo(() => {
    if (!syncedAt) return null;
    const minutes = Math.max(0, Math.round((Date.now() - syncedAt) / 60000));
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }, [syncedAt]);

  // Past an hour the cache has had time to refill with rows as stale as the
  // ones the last sync cleared, so the control stops being quiet grey.
  const stale = !!syncedAt && Date.now() - syncedAt > 60 * 60 * 1000;

  const handleClick = async () => {
    try {
      await syncMut.mutateAsync({ source });
      setSyncedAt(Date.now());
    } catch (err) {
      showError(getApiErrorMessage(err));
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={syncing}
      title={
        syncedAt
          ? `Cleared the NetSuite cache ${new Date(syncedAt).toLocaleString()}.\n` +
            "Click to clear it again and pull fresh rows from NetSuite."
          : "Click to clear the cached NetSuite results and pull fresh rows — the status columns can otherwise be up to an hour behind NetSuite."
      }
      // h-9 and the same shell as the Cash Flow reconcile control, so the two
      // read as one kind of thing: a cache age you can click to reset.
      className={`flex h-9 items-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-wait ${
        syncing
          ? "border-palette-sky-border bg-palette-sky-soft text-palette-sky-text"
          : stale
            ? "border-palette-amber-border bg-palette-amber-soft text-palette-amber-text hover:bg-palette-amber-muted"
            : "border-palette-gray-divider bg-white text-palette-gray-secondary hover:bg-palette-gray-subtle"
      }`}
    >
      <SyncIcon spinning={syncing} />
      {syncing ? "Syncing…" : age ? `Synced ${age}` : "Sync with NetSuite"}
    </button>
  );
}

function SyncIcon({ spinning }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`h-3.5 w-3.5 ${spinning ? "animate-spin" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      aria-hidden="true"
    >
      <path d="M14 8a6 6 0 1 1-1.76-4.24" strokeLinecap="round" />
      <path d="M14 2v4h-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
